import { useEffect } from 'react';
import { Datepicker, Input, Select, useLocale } from 'shared-ui';
import { Controller, useFormContext } from 'react-hook-form';

import { Form } from 'ui/form/Form';
import { ReportModal } from '../reportModal/ReportModal';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { AppMessages } from 'i18n/messages';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { useRenderMultiAutocompleteOption } from 'ui/multiAutocomplete/hooks/useRenderMultiAutocompleteOption';
import { MultiAutocomplete } from 'ui/multiAutocomplete/MultiAutocomplete';
import { ReactComponent as ReportEventsIcon } from 'assets/icons/report-events.svg';
import { ReportTile } from '../reportTile/ReportTile';
import * as Styled from '../Reports.styles';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { ReportErrorAlert } from '../reportErrorAlert';
import { handleValidFromDate } from 'helpers/handleValidFromDate';
import { handleValidToDate } from 'helpers/handleValidToDate';
import { getNow } from 'helpers/getNow';

import { EventsReportForm, EventsReportProps } from './EventsReport.types';

export const EventsReport = ({
  servers,
  isModalOpen,
  onModalClose,
  onModalOpen,
  terminals,
  isLoadingTerminals,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: EventsReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<EventsReportForm>();

  const { renderMultiAutocompleteOption } = useRenderMultiAutocompleteOption();
  const { serverOptions, modeOptions, eventsReportInputStateOptions } = useAppSelectOptions({ servers });
  const serwerId = watch('serwerId');
  const isServerSelected = !!serwerId && servers.some((server) => String(server.id) === String(serwerId));
  const serverPlaceholder = formatMessage({ id: AppMessages['reports.eventsReport.server.placeholder'] });
  const selectableServerOptions = [{ value: '', label: serverPlaceholder }, ...serverOptions];

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (hasNoDataError && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, onResetError]);

  const terminalsOptions = terminals.map((terminal) => ({
    label: terminal.nazwa,
    value: terminal.id,
    number: terminal.numerLogicznyKontrolera,
  }));

  return (
    <>
      {perms?.RAPORT_ZDARZEN?.view && (
        <ReportTile
          icon={<ReportEventsIcon />}
          name={formatMessage({ id: AppMessages['reports.eventsReport'] })}
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.eventsReport'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured || Object.keys(errors).length > 0}
        isDownloadButtonDisabled={isLoadingDownload || Object.keys(errors).length > 0}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="dataOd"
            control={control}
            render={({ field }) => {
              const dataDo = watch('dataDo');
              const maxAllowed = dataDo || new Date();

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataDo');
                  }}
                  label={formatMessage({ id: AppMessages['common.from'] })}
                  placeholderText={formatMessage({ id: AppMessages['reports.eventsReport.fromDate.placeholder'] })}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  maxDate={maxAllowed}
                  errorMessage={errors?.dataOd?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidFromDate}
                  customDateFormatOnOpen={() => field.value || getNow(dataDo)}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="dataDo"
            control={control}
            render={({ field }) => {
              const dataOd = watch('dataOd');

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataOd');
                  }}
                  maxDate={new Date()}
                  label={formatMessage({ id: AppMessages['common.to'] })}
                  placeholderText={formatMessage({ id: AppMessages['reports.eventsReport.toDate.placeholder'] })}
                  minDate={dataOd || undefined}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  errorMessage={errors?.dataDo?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidToDate}
                  customDateFormatOnOpen={() => field.value || getNow(dataOd)}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="nazwisko"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.lastName'] })}
                placeholder={formatMessage({ id: AppMessages['reports.eventsReport.lastName.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="nrEwidencyjny"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.registrationNumber'] })}
                placeholder={formatMessage({ id: AppMessages['reports.eventsReport.registrationNumber.placeholder'] })}
                {...field}
              />
            )}
          />

          <Controller
            name="numerKarty"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.cardNumber'] })}
                placeholder={formatMessage({ id: AppMessages['reports.eventsReport.cardNumber.placeholder'] })}
                {...field}
              />
            )}
          />

          <Styled.FieldContainer>
            <Controller
              name="komorki"
              control={control}
              render={({ field }) => (
                <OrganizationsTreeSelect
                  {...field}
                  multiple={false}
                  placeholder={formatMessage({ id: AppMessages['reports.eventsReport.cell.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.eventsReport.cell'] })}
                />
              )}
            />
          </Styled.FieldContainer>

          <Controller
            name="serwerId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value || ''}
                onChange={(event) => {
                  field.onChange(event);
                  setValue('tkds', []);
                }}
                label={formatMessage({ id: AppMessages['reports.eventsReport.server'] })}
                placeholder={serverPlaceholder}
                withHelperText
                options={selectableServerOptions}
              />
            )}
          />

          <Controller
            name="tkds"
            control={control}
            render={({ field }) => (
              <MultiAutocomplete
                {...field}
                withLimitedTags
                stopRotatePopupIndicator
                inputSize="big"
                limitTags={1}
                onChange={(_, option) => (option ? field.onChange(option) : field.onChange(null))}
                options={terminalsOptions}
                loading={isLoadingTerminals}
                disabled={!isServerSelected}
                label={formatMessage({ id: AppMessages['reports.eventsReport.reader'] })}
                placeholder={formatMessage({ id: AppMessages['reports.eventsReport.reader.placeholder'] })}
                renderOption={renderMultiAutocompleteOption}
              />
            )}
          />

          <Form.ControlledSelect
            name="tryb"
            control={control}
            label={formatMessage({ id: AppMessages['reports.eventsReport.mode'] })}
            placeholder={formatMessage({ id: AppMessages['reports.eventsReport.mode.placeholder'] })}
            withHelperText
            options={modeOptions}
          />

          <Form.ControlledSelect
            name="stanWejscia"
            control={control}
            label={formatMessage({ id: AppMessages['reports.eventsReport.inputState'] })}
            placeholder={formatMessage({ id: AppMessages['reports.eventsReport.inputState.placeholder'] })}
            withHelperText
            options={eventsReportInputStateOptions}
          />
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}
      </ReportModal>
    </>
  );
};
